import React, { useEffect, useState, useRef } from 'react'
import { Box, CircularProgress, Typography, LinearProgress, Chip } from '@mui/material'
import { styled } from '@mui/system'
import { useAppSelector } from '../hooks/useAppSelector'

interface LoadingStage {
  name: string
  progress: number
  status: 'pending' | 'loading' | 'complete' | 'error'
}

const LoadingContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  color: '#fff',
}))

const LoadingContent = styled(Box)({
  textAlign: 'center',
  maxWidth: '400px',
  width: '90%',
})

const StageContainer = styled(Box)({
  marginTop: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})

const StageItem = styled(Box)<{ status: string }>(({ status }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: '8px',
  backgroundColor:
    status === 'complete' ? 'rgba(76, 175, 80, 0.2)' :
      status === 'loading' ? 'rgba(33, 150, 243, 0.2)' :
        status === 'error' ? 'rgba(244, 67, 54, 0.2)' :
          'rgba(255, 255, 255, 0.1)',
  border: `1px solid ${status === 'complete' ? '#4CAF50' :
      status === 'loading' ? '#2196F3' :
        status === 'error' ? '#F44336' :
          'rgba(255, 255, 255, 0.2)'
    }`,
}))

const ProgressBar = styled(LinearProgress)({
  width: '100%',
  height: '8px',
  borderRadius: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  '& .MuiLinearProgress-bar': {
    borderRadius: '4px',
  },
})

export const OptimizedLoadingScreen: React.FC = () => {
  const loadingGame = useAppSelector((state) => state.game.loadingGame)
  const loadingProgress = useAppSelector((state) => state.game.loadingProgress)
  const roomState = useAppSelector((state) => state.room)

  // Detect owned custom room using Redux state/customRoomId
  const userId = useAppSelector((state) => state.user.userData?.id)
  const currentCustomRoomId = roomState.customRoomId || (typeof window !== 'undefined' ? (window as any).currentCustomRoomId : undefined)
  const isOwnedCustom = !!userId && !!currentCustomRoomId && `${userId}-` === String(currentCustomRoomId).slice(0, `${userId}-`.length)

  const [loadingStages, setLoadingStages] = useState<LoadingStage[]>([
    { name: isOwnedCustom ? 'Preparing Your Custom Room' : 'Loading Configuration', progress: 0, status: 'pending' },
    { name: 'Loading Character Assets', progress: 0, status: 'pending' },
    { name: 'Loading Background Assets', progress: 0, status: 'pending' },
    { name: isOwnedCustom ? 'Deferring Virtual Areas (Faster Start)' : '🔍 Checking Custom Override Virtual Areas', progress: 0, status: 'pending' },
    { name: isOwnedCustom ? 'Deferring Virtual Areas (Faster Start)' : '🔍 Checking Default Virtual Areas', progress: 0, status: 'pending' },
    { name: 'Loading Map Data', progress: 0, status: 'pending' },
    { name: 'Loading Tilesets', progress: 0, status: 'pending' },
    { name: isOwnedCustom ? 'Quick-Start Game Init' : 'Initializing Game', progress: 0, status: 'pending' },
    { name: 'Positioning Player', progress: 0, status: 'pending' },
  ])

  const [currentStageIndex, setCurrentStageIndex] = useState(0)
  const [playerReady, setPlayerReady] = useState(false)
  const spawnEnforceDispatched = useRef(false)
  const [loadingTips] = useState([
    'Assets are now loaded on-demand for faster startup times',
    'Only the assets you need are loaded when you need them',
    'Background loading ensures smooth transitions between maps',
    'Smart preloading predicts which assets you\'ll need next',
    'Optimized loading reduces memory usage and improves performance'
  ])
  const [currentTip, setCurrentTip] = useState(0)

  // Room configuration loading moved to Game.ts to avoid import issues

  // Listen for PLAYER_READY event
  useEffect(() => {
    const handlePlayerReady = (event: CustomEvent) => {
      console.log('[OptimizedLoadingScreen] ✅ Player ready event received:', {
        detail: event.detail,
        timestamp: Date.now(),
        loadingProgress,
        playerReady: playerReady
      })
      setPlayerReady(true)

      // Update the final stage to completed
      setLoadingStages(prev => prev.map((stage, index) => {
        if (index === prev.length - 1) { // Last stage (Positioning Player)
          return { ...stage, status: 'complete' as const, progress: 100 }
        }
        return stage
      }))
    }

    window.addEventListener('PLAYER_READY', handlePlayerReady as EventListener)

    return () => {
      window.removeEventListener('PLAYER_READY', handlePlayerReady as EventListener)
    }
  }, [loadingProgress, playerReady])

  // Update loading stages based on progress
  useEffect(() => {
    const newStages = [...loadingStages]

    // Map progress to different stages with more granular control
    if (loadingProgress >= 10) {
      newStages[0] = { ...newStages[0], progress: 100, status: 'complete' }
      setCurrentStageIndex(1)
    }
    if (loadingProgress >= 20) {
      newStages[1] = { ...newStages[1], progress: 100, status: 'complete' }
      setCurrentStageIndex(2)
    }
    if (loadingProgress >= 35) {
      newStages[2] = { ...newStages[2], progress: 100, status: 'complete' }
      setCurrentStageIndex(3)
    }
    if (loadingProgress >= 50) {
      newStages[3] = { ...newStages[3], progress: 100, status: 'complete' }
      setCurrentStageIndex(4)
    }
    if (loadingProgress >= 65) {
      newStages[4] = { ...newStages[4], progress: 100, status: 'complete' }
      setCurrentStageIndex(5)
    }
    if (loadingProgress >= 80) {
      newStages[5] = { ...newStages[5], progress: 100, status: 'complete' }
      setCurrentStageIndex(6)
    }
    if (loadingProgress >= 95) {
      newStages[6] = { ...newStages[6], progress: 100, status: 'complete' }
      setCurrentStageIndex(7)
    }
    if (loadingProgress >= 100 && playerReady) {
      newStages[7] = { ...newStages[7], progress: 100, status: 'complete' }
      newStages[8] = { ...newStages[8], progress: 100, status: 'complete' }
    } else if (loadingProgress >= 100 && !playerReady) {
      newStages[7] = { ...newStages[7], progress: 100, status: 'complete' }
      setCurrentStageIndex(8) // Show "Positioning Player" stage
    }

    // Update currently loading stage
    if (currentStageIndex < newStages.length) {
      const stageProgress = Math.min(100, ((loadingProgress - (currentStageIndex * 10)) / 10) * 100)
      newStages[currentStageIndex] = {
        ...newStages[currentStageIndex],
        progress: Math.max(0, stageProgress),
        status: stageProgress >= 100 ? 'complete' : 'loading'
      }
    }

    setLoadingStages(newStages)
  }, [loadingProgress, playerReady])

  // Rotate loading tips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % loadingTips.length)
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  // While loading the game, ensure the URL is neutral (not /dashboard or a menu route)
  const [slowConnection, setSlowConnection] = useState(false)

  // Track slow loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setSlowConnection(true)
    }, 5000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!loadingGame) return
    const neutralPath = '/room'
    if (typeof window !== 'undefined' && window.location.pathname !== neutralPath) {
      // Replace state so we don't clutter history
      window.history.replaceState({}, '', neutralPath)
    }
  }, [loadingGame])

  // Track when we enter "waiting for player" state and set up timeout fallback
  useEffect(() => {
    if (loadingProgress >= 100 && !playerReady && loadingGame) {
      // Log only once when entering this state
      console.log('[OptimizedLoadingScreen] ⏳ Loading complete, waiting for player to be ready...', {
        timestamp: Date.now(),
        loadingProgress,
        playerReady
      })

      // Set up fallback timeout (12 seconds)
      const timeoutId = setTimeout(() => {
        if (!playerReady && loadingGame) {
          console.warn('[OptimizedLoadingScreen] ⚠️ Timeout: PLAYER_READY event not received after 12 seconds. Auto-hiding loading screen.')
          setPlayerReady(true)

          // Update the final stage to completed
          setLoadingStages(prev => prev.map((stage, index) => {
            if (index === prev.length - 1) { // Last stage (Positioning Player)
              return { ...stage, status: 'complete' as const, progress: 100 }
            }
            return stage
          }))
        }
      }, 12000) // 12 second timeout

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [loadingProgress, playerReady, loadingGame])

  // When loading screen is about to hide, dispatch event so Game can enforce spawn position
  useEffect(() => {
    if (loadingProgress >= 100 && playerReady && !spawnEnforceDispatched.current) {
      spawnEnforceDispatched.current = true
      window.dispatchEvent(new CustomEvent('LOADING_SCREEN_HIDDEN', {
        detail: { timestamp: Date.now() }
      }))
    }
  }, [loadingProgress, playerReady])

  // Only hide loading screen when both loading is complete AND player is ready
  if (!loadingGame) {
    return null
  }

  // Hide loading screen only when player is ready
  if (loadingProgress >= 100 && playerReady) {
    return null
  }

  return (
    <LoadingContainer>
      <LoadingContent>
        <CircularProgress size={60} sx={{ color: '#2196F3', marginBottom: '20px' }} />

        <Typography variant="h4" sx={{ marginBottom: '10px', fontWeight: 'bold' }}>
          Loading SEVV
        </Typography>

        <Typography variant="subtitle1" sx={{ opacity: 0.8, marginBottom: '20px' }}>
          {slowConnection ? '(on some slow internet connections this might take some time)' : 'Please wait a second..'}
        </Typography>

        <ProgressBar variant="determinate" value={loadingProgress} />

        <Typography variant="body2" sx={{ marginTop: '10px', opacity: 0.7 }}>
          {Math.round(loadingProgress)}% Complete
        </Typography>

        <StageContainer>
          {loadingStages.map((stage, index) => (
            <StageItem key={index} status={stage.status}>
              <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                {stage.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {stage.status === 'complete' && (
                  <Chip
                    label="✓"
                    size="small"
                    sx={{
                      backgroundColor: '#4CAF50',
                      color: '#fff',
                      minWidth: '24px',
                      height: '20px'
                    }}
                  />
                )}
                {stage.status === 'loading' && (
                  <CircularProgress size={16} sx={{ color: '#2196F3' }} />
                )}
                {stage.status === 'pending' && (
                  <Chip
                    label="..."
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      minWidth: '24px',
                      height: '20px'
                    }}
                  />
                )}
              </Box>
            </StageItem>
          ))}
        </StageContainer>

        <Box sx={{ marginTop: '30px', padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
          <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
            💡 Tip: {loadingTips[currentTip]}
          </Typography>
        </Box>
      </LoadingContent>
    </LoadingContainer>
  )
}

export default OptimizedLoadingScreen 